/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import React from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { ContainedButton, MyIconButton, OutlinedButton } from "./buttons";
import { LoadStatus } from "../helpers";
import { useAppSelector } from "../store/hooks";

export function Popup(props: {
  open: boolean;
  title: string;
  fullWidth?: boolean;
  fullScreen?: boolean;

  warning?: string;
  buttonText?: string;
  buttonDisabled?: boolean;
  onConfirm?: () => void;
  cancelText?: string;
  onClose?: () => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
}): JSX.Element {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullScreen={props.fullScreen}
      fullWidth={props.fullWidth}
    >
      <DialogTitle className="row" style={{ justifyContent: "space-between" }}>
        {props.title}
        {props.fullScreen && props.onClose !== undefined ? (
          <MyIconButton aria="close dialog menu button" onClick={props.onClose}>
            <Close />
          </MyIconButton>
        ) : undefined}
      </DialogTitle>
      <DialogContent>{props.children}</DialogContent>
      <DialogActions>
        <Typography style={{ fontSize: 12 }}>{props.warning}</Typography>
        {props.onClose !== undefined ? (
          <OutlinedButton
            aria="close dialog menu button"
            onClick={props.onClose}
          >
            {props.cancelText || "Close"}
          </OutlinedButton>
        ) : undefined}
        <div style={{ flex: 1 }} />
        {props.onConfirm !== undefined ? (
          <ContainedButton
            aria="confirm dialog menu button"
            onClick={props.onConfirm}
            disabled={props.buttonDisabled}
          >
            {props.buttonText || "Confirm"}
          </ContainedButton>
        ) : undefined}
      </DialogActions>
    </Dialog>
  );
}

export function LoadingOverlay(): JSX.Element {
  const [isLoading, setIsLoading] = React.useState<string>();
  const { recordLoadState, recordSaveState, planSaveState, userTestSaveState } =
    useAppSelector((state) => state.userData);
  const { goalsLoadState, studyLoadState, surveyLoadState, dialogueLoadState } =
    useAppSelector((state) => state.data);

  React.useEffect(() => {
    if (
      goalsLoadState.status === LoadStatus.IN_PROGRESS ||
      studyLoadState.status === LoadStatus.IN_PROGRESS ||
      surveyLoadState.status === LoadStatus.IN_PROGRESS ||
      dialogueLoadState.status === LoadStatus.IN_PROGRESS ||
      recordLoadState.status === LoadStatus.IN_PROGRESS
    ) {
      setIsLoading("Loading...");
    } else if (
      recordSaveState.status === LoadStatus.IN_PROGRESS ||
      planSaveState.status === LoadStatus.IN_PROGRESS ||
      userTestSaveState.status === LoadStatus.IN_PROGRESS
    ) {
      setIsLoading("Saving...");
    } else {
      setIsLoading(undefined);
    }
  }, [
    goalsLoadState,
    studyLoadState,
    surveyLoadState,
    dialogueLoadState,
    recordLoadState,
    recordSaveState,
    planSaveState,
    userTestSaveState,
  ]);

  if (!isLoading) return <div />;
  return (
    <div
      className="column center"
      style={{
        position: "absolute",
        height: "100%",
        width: "100%",
        top: 0,
        left: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <Paper className="column center" style={{ padding: 10 }}>
        <CircularProgress />
        <Typography>{isLoading}</Typography>
      </Paper>
    </div>
  );
}
